import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function DeleteOrgDialog({ org, token, onClose, onDeleted }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [counts, setCounts] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!org) return;
    setCounts(null); setConfirmText("");
    axios.get(`${API}/superadmin/orgs/${org.id}/document-counts`, h)
      .then(({ data }) => setCounts(data))
      .catch(() => setCounts({ total: 0, breakdown: [] }));
  }, [org]); // eslint-disable-line
  const del = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/superadmin/orgs/${org.id}`, h);
      toast.success(`Prestataire « ${org.name} » supprimé (${data.total} document${data.total > 1 ? "s" : ""})`);
      onDeleted && onDeleted();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(false); }
  };
  const canDelete = confirmText.trim() === (org?.name || "").trim() && !busy;
  return (
    <Dialog open={!!org} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-950 text-white" data-testid="sa-delete-org-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-400">Supprimer définitivement ce prestataire</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Cette action est <b className="text-red-400">irréversible</b>. Toutes les données de « {org?.name} » seront effacées.
          </DialogDescription>
        </DialogHeader>
        {!counts ? (
          <p className="text-sm text-zinc-500" data-testid="sa-delete-counts-loading">Calcul des documents liés…</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-red-500/30 bg-red-500/[0.06] p-4">
              <p className="text-sm text-zinc-300">
                <b data-testid="sa-delete-total" className="text-2xl font-bold text-white">{counts.total}</b>{" "}
                document{counts.total > 1 ? "s" : ""} lié{counts.total > 1 ? "s" : ""} à cette entreprise ser{counts.total > 1 ? "ont" : "a"} supprimé{counts.total > 1 ? "s" : ""}.
              </p>
              {counts.breakdown?.length > 0 && (
                <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-zinc-400" data-testid="sa-delete-breakdown">
                  {counts.breakdown.map((b) => (
                    <li key={b.key} className="flex items-center justify-between border-b border-zinc-800/60 py-1">
                      <span>{b.label}</span><span className="font-medium text-zinc-200">{b.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Pour confirmer, tapez le nom du prestataire : <b className="text-white">{org?.name}</b></Label>
              <Input data-testid="sa-delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={org?.name} className="mt-1.5 h-10 border-zinc-800 bg-zinc-900 text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <Button data-testid="sa-delete-cancel" variant="outline" onClick={onClose} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800">Annuler</Button>
              <Button data-testid="sa-delete-confirm" onClick={del} disabled={!canDelete} className="rounded-full bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"><Trash2 className="mr-1.5 h-4 w-4" />Supprimer définitivement</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
